/*----------------------------------
- TLD PAGE: OTHER TLD PAGES
----------------------------------*/

/**
 * @docs docs/features/tld-intelligence
 * @rule The other public-set pages, published ones only, so the set stays one crawlable
 *       cluster, then the page's own alternates: its Markdown mirror, its agenda dates and, only
 *       while the public API is on (`apiUrl`, D5), its endpoint. Sibling links carry no tracking:
 *       `tld_page_cta_click` names no sibling target, and `hub` means the way back to the hub.
 */

/*----------------------------------
- DEPENDENCIES
----------------------------------*/

import { TLD_HUB_PATH, formatTldLabel, tldInsightMarkdownPath } from '@uniquedomains/components/contracts/tlds/public';
import { TLD_PAGE_CARD_ICONS } from '@uniquedomains/components/tlds/page/pageCatalog';
import TldPageCard from '@uniquedomains/components/tlds/page/TldPageCard';
import TldPagePill from '@uniquedomains/components/tlds/page/TldPagePill';

import { TLD_PAGE_CONTENT } from '@/client/catalogs/landing/tld/pageContent';
import { track } from '@/client/pages/landing/lib/analytics';

import { buildTldAgendaHref, listTldSiblingLinks } from '../../lib/presentation';

/*----------------------------------
- TYPES
----------------------------------*/

type TldSiblingLinksProps = {
    slug: string;
    /** The rest of the public set, in Product's neutral order. */
    relatedSlugs: readonly string[];
    publishedPaths: ReadonlySet<string> | null;
    apiUrl: string | null;
    onHubClick: () => void;
};

/*----------------------------------
- CONSTANTS
----------------------------------*/

const UNDERLINED_LINK_CLASS = 'text-[#4a4d4e] underline decoration-[#c4c2b6] underline-offset-4 hover:decoration-[#4a4d4e]';

/*----------------------------------
- COMPONENT
----------------------------------*/

export default function TldSiblingLinks({ slug, relatedSlugs, publishedPaths, apiUrl, onHubClick }: TldSiblingLinksProps) {
    const content = TLD_PAGE_CONTENT.insight.siblings;
    const siblings = listTldSiblingLinks(slug, relatedSlugs, publishedPaths);

    return (
        <TldPageCard tone="sand" icon={TLD_PAGE_CARD_ICONS.siblings} title={content.heading} testId="tld-page-siblings">
            <ul class="m-0 mt-3.5 flex list-none flex-wrap gap-1.5 p-0">
                {siblings.map((sibling) => (
                    <li key={sibling.slug}>
                        <a
                            href={sibling.href}
                            class="inline-flex min-h-8 items-center rounded-full bg-white px-[13px] text-[13.5px] font-bold text-[var(--ud-ink)] no-underline hover:underline"
                        >
                            {formatTldLabel(sibling.slug)}
                        </a>
                    </li>
                ))}
                <li>
                    <TldPagePill tone="ink" size="sm" href={TLD_HUB_PATH} onClick={onHubClick} testId="tld-page-every-tld">
                        {content.everyTld}
                    </TldPagePill>
                </li>
            </ul>
            <p class="mb-0 mt-4 flex flex-wrap gap-3 text-[13px] font-bold">
                <a href={tldInsightMarkdownPath(slug)} class={UNDERLINED_LINK_CLASS} data-testid="tld-page-markdown-link">{content.markdown}</a>
                <a href={buildTldAgendaHref(slug)} class={UNDERLINED_LINK_CLASS} data-testid="tld-page-agenda-link">{TLD_PAGE_CONTENT.insight.agendaLink(formatTldLabel(slug))}</a>
                {apiUrl ? (
                    <a
                        href={apiUrl}
                        class={UNDERLINED_LINK_CLASS}
                        data-testid="tld-page-api-link"
                        onClick={() => track('tld_page_cta_click', { cta: 'api', tld: slug, href: apiUrl })}
                    >
                        {content.api}
                    </a>
                ) : null}
            </p>
        </TldPageCard>
    );
}
